// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import React from 'react';
import { Button, Dropdown, Menu, MenuItem, useTranslate } from '@plesk/ui-library';

// AI answer styles the user can pick to influence the tone of the generated answer.
// Each id is both the sprite icon id and the `style` value sent to the backend.
//
// PPP-72220: this list must stay in sync with the keys of STYLE_OPTIONS in the PHP
// UserPromptDecorator, which owns the actual style->prompt mapping. Only the keys are
// duplicated here (as icon ids / the `style` token); the prompt text lives on the backend.
export const AI_STYLES = [
    'grandmother',
    'sergeant',
    'wizard',
    'pirate',
    'butler',
    'medium',
] as const;

export type AiStyle = typeof AI_STYLES[number];

const COLORED_ICONS_SPRITE = '/cp/theme/icons/24/colored-icons-sprite.svg';

const SpriteIcon = ({ id }: { id: string }) => (
    <span className="pul-icon pul-icon--size-16">
        <svg focusable="false"><use href={`${COLORED_ICONS_SPRITE}#${id}`} /></svg>
    </span>
);

type AiStylePickerProps = {
    enabled: boolean;
    value: AiStyle | null;
    onChange: (style: AiStyle | null) => void;
};

const AiStylePicker = ({ enabled, value, onChange }: AiStylePickerProps) => {
    const translate = useTranslate('components.copilot');

    if (!enabled) {
        return null;
    }

    if (value) {
        return (
            <Button
                icon={<SpriteIcon id={value} />}
                ghost
                tooltip={translate('reset-ai-style')}
                tooltipAsLabel
                onClick={() => onChange(null)}
            />
        );
    }

    return (
        <Dropdown
            menu={
                <Menu className="plesk-copilot-style-menu">
                    {AI_STYLES.map(style => (
                        <MenuItem
                            key={style}
                            icon={<SpriteIcon id={style} />}
                            tooltip={translate(`ai-style.${style}`)}
                            onClick={() => onChange(style)}
                        />
                    ))}
                </Menu>
            }
        >
            <Button
                icon={<SpriteIcon id="magic-wand" />}
                ghost
                tooltip={translate('apply-ai-style')}
                tooltipAsLabel
            />
        </Dropdown>
    );
};

export default AiStylePicker;
